"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { resolveBuildUrl, type DesktopBuild } from "./releases";

export type DownloadStatus =
  | "idle"
  | "preparing"
  | "downloading"
  /** The stream died mid-way — the bytes we already have can be resumed. */
  | "interrupted"
  | "completed"
  | "error";

export interface DownloadState {
  status: DownloadStatus;
  build: DesktopBuild | null;
  receivedBytes: number;
  /** null when the server does not expose a size. */
  totalBytes: number | null;
  /** 0..100, null while the total size is unknown. */
  progress: number | null;
  bytesPerSecond: number | null;
  secondsLeft: number | null;
}

const IDLE_STATE: DownloadState = {
  status: "idle",
  build: null,
  receivedBytes: 0,
  totalBytes: null,
  progress: null,
  bytesPerSecond: null,
  secondsLeft: null,
};

/** How far back we look when estimating speed, so the ETA does not jitter. */
const SPEED_WINDOW_MS = 3000;
/** Repainting on every chunk is wasteful on a fast connection. */
const UI_UPDATE_INTERVAL_MS = 100;

interface Session {
  build: DesktopBuild;
  chunks: Uint8Array[];
  received: number;
  total: number | null;
}

/**
 * The installer is buffered in the tab and handed to the browser only once the
 * progress bar reaches 100%. Streaming straight to disk (showSaveFilePicker)
 * would open the OS save dialog on click and turn this back into an ordinary
 * browser download, which is exactly the experience we are replacing.
 */
const saveBlob = (chunks: Uint8Array[], fileName: string) => {
  const url = URL.createObjectURL(
    new Blob(chunks as BlobPart[], { type: "application/octet-stream" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  // Safari needs the object URL alive until the download is picked up.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** "bytes 1024-2047/128000" -> 128000 */
const parseRangeTotal = (contentRange: string | null) => {
  const total = contentRange?.match(/\/(\d+)\s*$/)?.[1];
  return total ? Number(total) : null;
};

const toProgress = (received: number, total: number | null) =>
  total ? Math.min((received / total) * 100, 100) : null;

export const useFileDownload = () => {
  const [state, setState] = useState<DownloadState>(IDLE_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const run = useCallback(async (session: Session, isResume: boolean) => {
    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      status: "preparing",
      build: session.build,
      receivedBytes: session.received,
      totalBytes: session.total,
      progress: toProgress(session.received, session.total),
      bytesPerSecond: null,
      secondsLeft: null,
    });

    const samples = [{ at: performance.now(), bytes: session.received }];
    let lastPaintAt = 0;

    try {
      const response = await fetch(resolveBuildUrl(session.build), {
        signal: controller.signal,
        cache: "no-store",
        headers:
          isResume && session.received > 0
            ? { Range: `bytes=${session.received}-` }
            : undefined,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      if (response.status === 206) {
        session.total =
          parseRangeTotal(response.headers.get("Content-Range")) ??
          session.total;
      } else {
        // A full body: either a fresh start, or the server ignored our Range.
        session.chunks = [];
        session.received = 0;
        const length = Number(response.headers.get("Content-Length"));
        session.total = Number.isFinite(length) && length > 0 ? length : null;
      }

      setState((prev) => ({
        ...prev,
        status: "downloading",
        receivedBytes: session.received,
        totalBytes: session.total,
        progress: toProgress(session.received, session.total),
      }));

      const reader = response.body.getReader();

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;

        session.received += value.byteLength;
        session.chunks.push(value);

        const now = performance.now();
        samples.push({ at: now, bytes: session.received });
        while (samples.length > 2 && now - samples[0]!.at > SPEED_WINDOW_MS) {
          samples.shift();
        }

        if (now - lastPaintAt < UI_UPDATE_INTERVAL_MS) continue;
        lastPaintAt = now;

        const oldest = samples[0]!;
        const elapsed = (now - oldest.at) / 1000;
        const speed =
          elapsed > 0 ? (session.received - oldest.bytes) / elapsed : null;
        const remaining = session.total
          ? Math.max(session.total - session.received, 0)
          : null;

        setState((prev) => ({
          ...prev,
          receivedBytes: session.received,
          progress: toProgress(session.received, session.total),
          bytesPerSecond: speed,
          secondsLeft:
            speed && speed > 0 && remaining !== null ? remaining / speed : null,
        }));
      }

      saveBlob(session.chunks, session.build.fileName);

      abortRef.current = null;
      sessionRef.current = null;
      setState({
        status: "completed",
        build: session.build,
        receivedBytes: session.received,
        totalBytes: session.total ?? session.received,
        progress: 100,
        bytesPerSecond: null,
        secondsLeft: null,
      });
    } catch (error) {
      if ((error as Error)?.name === "AbortError") return;

      console.error("Yolk Desktop download failed", error);
      abortRef.current = null;

      // Keep the bytes we already have so Resume can pick up where it stopped.
      const canResume = session.received > 0;
      if (!canResume) sessionRef.current = null;

      setState((prev) => ({
        ...prev,
        status: canResume ? "interrupted" : "error",
        bytesPerSecond: null,
        secondsLeft: null,
      }));
    }
  }, []);

  const start = useCallback(
    (build: DesktopBuild) => {
      abortRef.current?.abort();

      const session: Session = {
        build,
        chunks: [],
        received: 0,
        total: build.sizeBytes ?? null,
      };
      sessionRef.current = session;
      void run(session, false);
    },
    [run],
  );

  const resume = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    void run(session, true);
  }, [run]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    sessionRef.current = null;
    setState(IDLE_STATE);
  }, []);

  return { ...state, start, resume, cancel };
};
